import React, { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { TAdminDetails, TEditAdminDetailsReduxParams } from '~types/feat/admin';
import combinedActions from '~redux/combinedActions';

import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';

import UtilFunctions from '~utils/functions';

import * as FormStyles from './_adminsAdminDetailsForm.module.scss';
import { AuthUserStatusEnum, BranchStatusEnum } from '~utils/enums';

type TAdminsAdminDetailsForm = {
    submitButtonLabel: string;
    error: string;
    onSubmit: (data: TEditAdminDetailsReduxParams) => void;
    isEditing?: boolean;
    inputIsDisabled?: boolean;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    actionButtonIsDisabled?: boolean;
    initialAdminDetails?: TAdminDetails;
}

const AdminsAdminDetailsForm: FC<TAdminsAdminDetailsForm> = ({
    submitButtonLabel,
    error,
    onSubmit,
    isEditing,
    inputIsDisabled,
    actionButtonIsLoading,
    actionButtonClassName,
    actionButtonIsDisabled,
    initialAdminDetails,
}) => {
    const dispatch = useAppDispatch();

    const {
        Misc: {
            blockEnterKey,
            dateTimeFormat,
        },
    } = UtilFunctions;

    const allRoles = useAppSelector(state => state.admin.allRoles);
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const { allBranchAccess, branchCode, userId } = useAppSelector(state => state.auth.userDetails);

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            formik.setFieldValue('branch', branchesList.find((branch) => branch.branchCode === branchCode)?.branchName);
        }
    }, [branchesList]);

    useEffect(() => {
        dispatch(combinedActions.adminGetAllRolesAttempt());
    }, []);

    useEffect(() => {
        if (!isEditing) {
            formik.resetForm();
        }
    }, [isEditing]);

    const AdminsAdminDetailsFormValidation = Yup.object().shape({
        branch: Yup.string().oneOf(branchesList.map(branch => branch.branchName), 'Invalid branch').label('Branch').required('Branch is required'),
        role: Yup.string().oneOf(allRoles.map(role => role.roleName), 'Invalid role').label('Role').required('Role is required'),
        status: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().oneOf(Object.values(AuthUserStatusEnum).filter(e => Number.isNaN(Number(e))) as string[]).label('Status').required('Status is required'),
        referralCode: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchSpecialCharacters().label('Referral Code'),
    });

    const AdminsAdminDetailsFormInitialValues = {
        branch: branchesList.find(branch => branch.branchId === initialAdminDetails?.branchId)?.branchName || '',
        role: allRoles.find(role => role.roleId === initialAdminDetails?.roleId)?.roleName || '',
        status: initialAdminDetails?.status ? AuthUserStatusEnum[initialAdminDetails?.status] as keyof typeof AuthUserStatusEnum : '',
        referralCode: initialAdminDetails?.referralCode || '',
    };

    const formik = useFormik({
        initialValues: AdminsAdminDetailsFormInitialValues,
        validationSchema: AdminsAdminDetailsFormValidation,
        enableReinitialize: true,
        onSubmit: (values) => {
            const {
                role,
                branch,
                status,
                ...restOfValues
            } = values;

            const roleId = allRoles.find(item => item.roleName === role)?.roleId;
            const branchId = branchesList.find(item => item.branchName === branch)?.branchId;

            if (!roleId || !branchId) return;

            if (initialAdminDetails?.userId == userId){
                onSubmit({
                    userId: initialAdminDetails?.userId || 0,
                    branchId,
                    status: AuthUserStatusEnum[status as keyof typeof AuthUserStatusEnum] || AuthUserStatusEnum.Active,
                    ...restOfValues,
                });

                return;
            }

            onSubmit({
                userId: initialAdminDetails?.userId || 0,
                roleId,
                branchId,
                status: AuthUserStatusEnum[status as keyof typeof AuthUserStatusEnum] || AuthUserStatusEnum.Active,
                ...restOfValues,
            });
        },
    });

    const shouldDisableInput = formik.values.status === AuthUserStatusEnum[AuthUserStatusEnum.Deactivated];

    const branchOptions = branchesList.filter(branch => branch.status === BranchStatusEnum.Active).map(branch => {
        return {
            label: branch.branchName,
            value: branch.branchId,
        };
    });

    const roleOptions = allRoles.map(role => {
        return {
            label: role.roleName,
            value: role.roleId,
        };
    });

    const statusOptions = Object.values(AuthUserStatusEnum).filter(e => Number.isNaN(Number(e))).map(key => {
        return {
            label: key as string,
            value: key as string,
        };
    });

    const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Reset the form if the status is changed to not deactivated and changed back to deactivated (not allowed to update details if status is deactivated)
        if (e.target.value === AuthUserStatusEnum[AuthUserStatusEnum.Deactivated]
            && initialAdminDetails?.status === AuthUserStatusEnum.Deactivated
            && formik.values.status !== AuthUserStatusEnum[AuthUserStatusEnum.Deactivated]
        ) {
            toast.info('Form is reset');
            formik.resetForm();
        }

        formik.setFieldValue('status', e.target.value);
    };

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <LabelledInput
                    id='userId'
                    name='userId'
                    label='User ID :'
                    value={initialAdminDetails?.userId.toString() || ''}
                    disabled={true}
                />

                <LabelledInput
                    id='sAMAccountName'
                    name='sAMAccountName'
                    label='SAM Account Name :'
                    value={initialAdminDetails?.sAMAccountName || ''}
                    disabled={true}
                />

                <LabelledInput
                    id='displayName'
                    name='displayName'
                    label='Display Name :'
                    value={initialAdminDetails?.displayName || ''}
                    disabled={true}
                />

                <LabelledInput
                    id='email'
                    name='email'
                    label='Email :'
                    value={initialAdminDetails?.email || ''}
                    disabled={true}
                />

                <LabelledDropdown
                    id='branch'
                    name='branch'
                    label='Branch* :'
                    options={branchOptions}
                    value={formik.values.branch || ''}
                    error={formik.touched.branch ? formik.errors.branch : undefined}
                    onChange={(e) => formik.setFieldValue('branch', e.target.value)}
                    autoComplete='off'
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput || !allBranchAccess}
                    optionSelected={!!branchOptions.find(option => option.label === formik.values.branch)}
                />

                <LabelledDropdown
                    id='role'
                    name='role'
                    label='Role* :'
                    options={roleOptions}
                    value={formik.values.role || ''}
                    error={formik.touched.role ? formik.errors.role : undefined}
                    onChange={(e) => formik.setFieldValue('role', e.target.value)}
                    autoComplete='off'
                    onBlur={formik.handleBlur}
                    disabled={initialAdminDetails?.userId == userId ? true : false}
                    optionSelected={!!roleOptions.find(option => option.label === formik.values.role)}
                />

                <LabelledInput
                    id='referralCode'
                    name='referralCode'
                    label='Referral Code :'
                    value={formik.values.referralCode}
                    error={formik.touched.referralCode ? formik.errors.referralCode : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled || shouldDisableInput}
                />

                <LabelledDropdown
                    id='status'
                    name='status'
                    label='Status* :'
                    options={statusOptions}
                    value={formik.values.status || ''}
                    error={formik.touched.status ? formik.errors.status : undefined}
                    onChange={handleStatusChange}
                    autoComplete='off'
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                    optionSelected={!!statusOptions.find(option => option.label === formik.values.status)}
                />

                <LabelledInput
                    id='createdAt'
                    name='createdAt'
                    label='Created At :'
                    value={initialAdminDetails?.createdAt ? dateTimeFormat(initialAdminDetails?.createdAt) : ''}
                    disabled={true}
                />

                <LabelledInput
                    id='updatedAt'
                    name='updatedAt'
                    label='Updated At :'
                    value={initialAdminDetails?.updatedAt ? dateTimeFormat(initialAdminDetails?.updatedAt) : ''}
                    disabled={true}
                />

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={actionButtonIsLoading || actionButtonIsDisabled}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default AdminsAdminDetailsForm;
